import React, { useCallback, useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';

import { useAuth } from '../../contexts/AuthContext';
import { navigateToSpend } from '../../core/navigation/mainTabLinks';
import type { MainTabParamList } from '../../core/navigation/types';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { useBillingActions } from '../../hooks/useBillingActions';
import { ApiError } from '../../services/apiClient';
import {
  listSpendAlerts,
  suggestSpendAlerts,
  type ChannelReadiness,
  type SpendAlertSuggestion,
} from '../../services/spendAlertService';
import { subscribeSpendAlertsChanged } from '../../services/spendAlertsBus';
import {
  AnalyzeQuotaModal,
  type AnalyzeQuotaInfo,
} from '../billing/AnalyzeQuotaModal';
import { GlassCard } from '../ui/GlassCard';
import { RefreshButton } from '../ui/RefreshButton';
import { SetSpendAlertModal } from './SetSpendAlertModal';

/**
 * Home teaser for cut-spend alerts — Pro can set; free opens upgrade.
 */
export function HomeSpendAlertsCard() {
  const { isAuthenticated, user } = useAuth();
  const navigation = useNavigation<BottomTabNavigationProp<MainTabParamList>>();
  const { upgradeToPro, busy: upgradeBusy } = useBillingActions();
  const isPro = user?.plan === 'pro';

  const [enabledCount, setEnabledCount] = useState(0);
  const [topIdea, setTopIdea] = useState<SpendAlertSuggestion | null>(null);
  const [channels, setChannels] = useState<ChannelReadiness | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [proGate, setProGate] = useState<AnalyzeQuotaInfo | null>(null);

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setEnabledCount(0);
      return;
    }
    try {
      const [res, ideas] = await Promise.all([
        listSpendAlerts(),
        suggestSpendAlerts(new Date().getTimezoneOffset()).catch(() => null),
      ]);
      setEnabledCount((res.alerts ?? []).filter((a) => a.enabled).length);
      setChannels(res.channels ?? ideas?.channels ?? null);
      const first = (ideas?.suggestions ?? []).find((s) => !s.alreadyEnabled);
      setTopIdea(first ?? ideas?.suggestions?.[0] ?? null);
    } catch (err) {
      if (!(err instanceof ApiError)) {
        // ignore
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => subscribeSpendAlertsChanged(() => void load()), [load]);

  if (!isAuthenticated) return null;

  const open = () => {
    if (!isPro) {
      setProGate({
        title: 'Pro unlocks spend alerts',
        message:
          'See ideas on Daily spend, then get coached before dining, subscriptions, and other high-spend windows.',
        upgradeRequired: true,
      });
      return;
    }
    setModalOpen(true);
  };

  return (
    <>
      <GlassCard style={styles.card} accessibilityLabel="Spend alerts">
        <View style={styles.row}>
          <View style={styles.textCol}>
            <View style={styles.kickerRow}>
              <Text style={styles.kicker}>Daily spend · Alerts</Text>
              <RefreshButton onPress={() => void load()} compact />
            </View>
            <Text style={styles.title}>
              {enabledCount > 0
                ? `${enabledCount} active spend alert${enabledCount === 1 ? '' : 's'}`
                : topIdea
                  ? `${topIdea.categoryLabel} on ${topIdea.dayLabel}s`
                  : 'Cut spend before it happens'}
            </Text>
            <Text style={styles.sub}>
              {topIdea
                ? topIdea.reason
                : isPro
                  ? 'AI will recommend windows from your last 90 days of spend.'
                  : 'Pro nudges you before dining, subscriptions, and more.'}
            </Text>
          </View>
          <Pressable
            onPress={open}
            style={({ pressed }) => [styles.cta, pressed && styles.ctaPressed]}
            accessibilityRole="button"
            accessibilityLabel={isPro ? 'Set spend alert' : 'Upgrade for spend alerts'}
          >
            <Text style={styles.ctaText}>{isPro ? 'Set' : 'Pro →'}</Text>
          </Pressable>
        </View>
        {enabledCount > 0 ? (
          <Pressable
            onPress={() => navigateToSpend(navigation)}
            accessibilityRole="button"
            accessibilityLabel="Manage alerts on Daily spend"
          >
            <Text style={styles.link}>Manage on Daily spend →</Text>
          </Pressable>
        ) : null}
      </GlassCard>

      <SetSpendAlertModal
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        channels={channels}
        onSaved={() => void load()}
      />

      <AnalyzeQuotaModal
        visible={Boolean(proGate)}
        info={proGate}
        onDismiss={() => setProGate(null)}
        upgradeBusy={upgradeBusy}
        source="spend_alerts"
        onUpgrade={() => {
          setProGate(null);
          void upgradeToPro('spend_alerts');
        }}
      />
    </>
  );
}

const styles = StyleSheet.create({
  card: { gap: spacing.sm },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  textCol: { flex: 1, minWidth: 0, gap: 4 },
  kickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  kicker: {
    ...typography.caption,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: colors.accent,
  },
  title: {
    ...typography.headline,
    fontSize: 16,
    color: colors.text,
  },
  sub: {
    ...typography.caption,
    fontSize: 12,
    color: colors.textMuted,
    lineHeight: 16,
  },
  cta: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: radii.pill,
    backgroundColor: 'rgba(61, 214, 164, 0.16)',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(61, 214, 164, 0.4)',
  },
  ctaPressed: { opacity: 0.85 },
  ctaText: {
    ...typography.caption,
    fontWeight: '800',
    color: colors.accent,
  },
  link: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
  },
});
